import { useCallback, useEffect, useRef, useState } from 'react';
import { type UseGetByIdHookResult } from '@saviynt/biz-entity-catalog';

import { getClientById } from '../data-sources';
import { type Client } from '../types';

// Module-level counter that the list page can bump after a successful update.
// This forces the hook to refetch even when BEC passes the same id (BEC's
// updateItem doesn't clear editingItemId, so reopening the same client after
// save would otherwise serve stale cached data).
let globalFetchGeneration = 0;

/** Bump the fetch generation so the next useClientGetById call refetches. */
export function invalidateClientGetById(): void {
  globalFetchGeneration += 1;
}

/**
 * BizEntityCatalog getById hook adapter for client credentials.
 * Fetches and normalizes a single client (custom_claims → customClaims).
 */
export function useClientGetById(
  id: string | number | null
): UseGetByIdHookResult<Client> {
  const [data, setData] = useState<Client | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Capture the generation at the time of the last successful fetch so we can
  // detect when it's been invalidated.
  const fetchedGenerationRef = useRef(globalFetchGeneration);
  const pendingRefetchRef = useRef(false);

  // On every render, check if the generation has been bumped since our last
  // fetch. If so, clear stale data and flag for refetch.
  if (
    id !== null &&
    data !== undefined &&
    fetchedGenerationRef.current !== globalFetchGeneration
  ) {
    setData(undefined);
    pendingRefetchRef.current = true;
    fetchedGenerationRef.current = globalFetchGeneration;
  }

  const fetchClient = useCallback(async () => {
    if (id === null) {
      setData(undefined);

      return;
    }

    setIsLoading(true);
    setError(null);
    setData(undefined);

    try {
      const client = await getClientById(String(id));

      setData(client);
      fetchedGenerationRef.current = globalFetchGeneration;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const refetch = useCallback(() => {
    fetchClient().catch(() => {});
  }, [fetchClient]);

  // Primary fetch: fires when id changes
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Handle invalidation-triggered refetch after render-phase state clear
  useEffect(() => {
    if (pendingRefetchRef.current) {
      pendingRefetchRef.current = false;
      refetch();
    }
  });

  return { data, isLoading, error, refetch };
}
